import { toRefs, ref, computed } from '@vue/composition-api';
import { NAME_TAG } from '../const';
import { useStdLocation } from './../../location/hooks/index';
import { usePropLanguageListName } from './../../languages/hooks';
import { useGenderName } from './../../gender/hooks';
import { usePlatformName } from './../../operating-system/hooks';
import { dayjs } from '@tt4b/ttam-utils';
import { i18n } from './../../../../i18n';
import { ScheduleType, SmartBidType, PromotionFlowType, NO_LIMIT, OptimizeGoal, EcommType } from '@tt4b/ttam-enums';
import { useStdAdgroupOptimizationGoal } from '@tt4b/ttam-hooks';

export function getCountryTag(adgroupData) {
  const { getLocationNameFromId } = useStdLocation({
    adgroupData,
  });
  const getContent = valueArr => {
    const names = getLocationNameFromId(valueArr);
    // 少于等于3个地名，直接&拼接显示；超过3个地名，只显示3个，末尾写 and xx more
    if (names.length <= 3) {
      return names.join('&');
    }
    return `${names.slice(0, 3).join('&')} ${i18n.t('ad_group_name_tag_content_too_long', { n: names.length - 3 })}`;
  };

  let content = '';
  const { country, particleLocations } = adgroupData.value;
  // 两者相等，即选择了N个国家（每个国家里都是全选城市）
  if (country.length === particleLocations.length) {
    content = getContent(adgroupData.value.country);
  } else {
    // 否则展示里面的城市的名称
    content = getContent(adgroupData.value.particleLocations);
  }
  return {
    type: NAME_TAG.country,
    content,
  };
}

export function getLangTag(adgroupData) {
  const { languageListName } = usePropLanguageListName({
    adgroupData,
  });
  const isCLA = adgroupData.value.promotionFlowType === PromotionFlowType.CLA;
  const getContent = () => {
    if (!languageListName.value.length) {
      return !isCLA ? i18n.t('module_common_all') : '';
    }
    if (languageListName.value.length <= 3) {
      return languageListName.value.join('&');
    }
    return `${languageListName.value.slice(0, 3).join('&')} ${i18n.t('ad_group_name_tag_content_too_long', {
      n: languageListName.value.length - 3,
    })}`;
  };
  const content = getContent();
  return {
    type: NAME_TAG.language,
    content,
  };
}

export function getGenderTag(adgroupData) {
  const { genderName } = useGenderName({
    gender: ref(adgroupData.value.gender),
  });
  const isCLA = adgroupData.value.promotionFlowType === PromotionFlowType.CLA;
  return {
    type: NAME_TAG.gender,
    content: !isCLA ? genderName.value : '',
  };
}

export function getPlatformTag(adgroupData) {
  const { platformName } = usePlatformName(ref(adgroupData.value.platform));
  const isCLA = adgroupData.value.promotionFlowType === PromotionFlowType.CLA;
  return {
    type: NAME_TAG.os,
    content: !isCLA ? platformName.value : '',
  };
}

export function getStrategyTag(adgroupData, campaignData) {
  const type = NAME_TAG.bid_strategy;
  const { isOptGoalValue } = useStdAdgroupOptimizationGoal({
    adgroupFormData: adgroupData,
  });

  if (adgroupData.value.bid) {
    return { type, content: i18n.t('module_bid_strategy_bid_cap') };
  }
  if (adgroupData.value.roasBid > 0 && adgroupData.value.roasStatus) {
    return { type, content: i18n.t('module_bid_strategy_roas_title') };
  }
  if (isOptGoalValue.value && adgroupData.value.smartBidType === SmartBidType.NO_BID) {
    return {
      type,
      content:
        campaignData.value.ecommType === EcommType.SHOP
          ? i18n.t('module_bid_strategy_highest_value_title_v2')
          : i18n.t('module_bid_strategy_highest_value_title'),
    };
  }
  if (adgroupData.value.cpaBid) {
    return { type, content: i18n.t('module_bid_strategy_costcap') };
  }
  if (adgroupData.value.smartBidType === SmartBidType.MAX_CONVERTION) {
    return { type, content: i18n.t('module_bid_strategy_maxcv') };
  }
  return adgroupData.value?.optimizeGoal !== OptimizeGoal.NONE
    ? { type, content: i18n.t('module_bid_strategy_lowest_cost_new') }
    : { type, content: i18n.t('module_bid_strategy') };
}

export function getStartTimeTag(adgroupData) {
  return {
    type: NAME_TAG.st,
    content: dayjs(adgroupData.value.startTime).format('YYYYMMDD'),
  };
}

export function getEndTimeTag(adgroupData) {
  const content =
    adgroupData.value.scheduleType !== ScheduleType.CONTINOUSLY
      ? dayjs(adgroupData.value.endTime).format('YYYYMMDD')
      : i18n.t('module_budget_duration_continuous');
  return {
    type: NAME_TAG.et,
    content,
  };
}

export function getAgeTag(adgroupData) {
  const isCLA = adgroupData.value.promotionFlowType === PromotionFlowType.CLA;
  const { age } = adgroupData.value;
  const ageStr =
    age[0] === NO_LIMIT
      ? i18n.t('adgroup_no_limit')
      : age
          .sort((age1, age2) => age1.replace(',', '') - age2.replace(',', ''))
          .map(item => item.replace(',', '-'))
          .join('&');
  return {
    type: NAME_TAG.age,
    content: !isCLA ? ageStr : '',
  };
}

// 用于名称模板弹窗内部感知当前选中的每个tag的对应值，从adgroupData中获取拼凑而成
export function useTagMapList(props) {
  const { adgroupData, campaignData } = toRefs(props);
  return {
    tagMapList: computed(() => [
      getCountryTag(adgroupData),
      getLangTag(adgroupData),
      getGenderTag(adgroupData),
      getPlatformTag(adgroupData),
      getStrategyTag(adgroupData, campaignData),
      getStartTimeTag(adgroupData),
      getEndTimeTag(adgroupData),
      getAgeTag(adgroupData),
    ]),
  };
}
